import { FilterChip } from '@liveauctioneers/caterwaul-components/lib/FilterChip/FilterChip';
import SearchInput from '@liveauctioneers/caterwaul-components/lib/Input/SearchInput/SearchInput';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { debounce } from 'es-toolkit';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { useAppSelector } from '@/redux/hooks';
import { allGeoAreasSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { GeoArea } from '@/types/GeoArea';
import { getAuctionsNearMePageUrl, getAuctionsNearMeSearchUrl } from '@/utils/urls';

import { MapBounds } from '../GoogleMap/AuctionsNearMeGoogleMap';
import { getGeocoder } from './helpers/getGeocoder';

export type AuctionsNearMeSearchInputProps = {
    currentGeoArea: GeoArea | undefined | null;
    onSearchResults: (bounds: MapBounds) => void;
    parentSearchTerm: string;
    setGeocoderFailed: (failed: boolean) => void;
    setPageTitleSearchTerm: (term: string) => void;
};

export const AuctionsNearMeSearchInput = ({
    currentGeoArea,
    onSearchResults,
    parentSearchTerm,
    setGeocoderFailed,
    setPageTitleSearchTerm,
}: AuctionsNearMeSearchInputProps): ReactElement => {
    const google = typeof window !== 'undefined' ? window.google : null;
    const navigate = useNavigate();
    const { formatMessage, locale } = useIntl();
    const placeholder = formatMessage({
        id: 'auctionsNearMe.searchLocationPlaceholder',
    });
    const allGeoAreas = useAppSelector(allGeoAreasSelector);
    const geoAreaOptions = allGeoAreas.map((area) => area.geoAreaName);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState('');

    // Sync local state when parent's searchTerm is cleared (e.g., navigating back to base page)
    useEffect(() => {
        if (parentSearchTerm === '') {
            setSearchTerm('');
            setInputValue('');
        }
    }, [parentSearchTerm]);

    const provideBoundsViaGeocoder = (selectedOption: string): void => {
        const geocoder = getGeocoder(google);
        geocoder.geocode({ address: selectedOption }, (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || results.length === 0) {
                setGeocoderFailed(true);
                // Do not navigate on failure - stay on current page
                return;
            }
            const { viewport } = results[0].geometry;

            if (!viewport) {
                setGeocoderFailed(true);
                return;
            }

            const ne = viewport.getNorthEast();
            const sw = viewport.getSouthWest();

            const northEast = {
                lat: ne.lat(),
                lng: ne.lng(),
            };
            const southWest = {
                lat: sw.lat(),
                lng: sw.lng(),
            };

            // Geocoding succeeded
            setGeocoderFailed(false);
            onSearchResults({
                northEast,
                southWest,
            });

            // Navigate to custom search URL
            const searchUrl = getAuctionsNearMeSearchUrl(selectedOption);

            navigate(searchUrl);
        });
    };

    const provideBoundsViaGeoArea = (selectedOption: string): void => {
        const geoArea = allGeoAreas.find((geoArea) => geoArea.geoAreaName.toString() === selectedOption);

        if (!geoArea) {
            return;
        }

        const { northEastLatitude, northEastLongitude, southWestLatitude, southWestLongitude } = geoArea;
        const northEast = {
            lat: northEastLatitude,
            lng: northEastLongitude,
        };
        const southWest = {
            lat: southWestLatitude,
            lng: southWestLongitude,
        };
        setGeocoderFailed(false);
        onSearchResults({
            northEast,
            southWest,
        });

        // Use new URL format with GeoArea object
        const url = getAuctionsNearMePageUrl(geoArea);
        navigate(url);
    };

    const provideBoundsViaDefault = (): void => {
        const defaultBounds = locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds;
        setGeocoderFailed(false);
        onSearchResults(defaultBounds);
    };

    const onChange = (event): void => {
        setInputValue(event.target.value);
    };

    const onSubmit = (selectedOption: string): void => {
        if (!google) {
            return;
        }
        setSearchTerm(selectedOption);
        setPageTitleSearchTerm(selectedOption);
        if (geoAreaOptions.includes(selectedOption)) {
            provideBoundsViaGeoArea(selectedOption);
        } else if (selectedOption) {
            provideBoundsViaGeocoder(selectedOption);
        } else {
            provideBoundsViaDefault();
        }
    };

    const navigateToMainPage = () => {
        setInputValue('');
        setSearchTerm('');
        setPageTitleSearchTerm('');
        setGeocoderFailed(false);
        provideBoundsViaDefault();
        navigate(getAuctionsNearMePageUrl());
    };

    const currentSearchTerm = searchTerm || currentGeoArea?.geoAreaName;

    return (
        <section className="flex flex-col smMin:flex-row">
            <div className="mr-xs w-full smMin:w-[184px]">
                <SearchInput
                    aria-label={formatMessage({
                        id: 'auctionsNearMe.ariaLabel.searchInput',
                    })}
                    controlled
                    hideButton
                    onChange={onChange}
                    onSubmit={debounce(onSubmit, 300)}
                    options={geoAreaOptions}
                    placeholder={placeholder}
                    small
                    value={inputValue}
                    wide
                />
            </div>
            {Boolean(currentSearchTerm) && (
                <span className="smMax:mb-xs smMax:mr-0 smMax:mt-xs">
                    <FilterChip onRemove={navigateToMainPage}>{currentSearchTerm}</FilterChip>
                </span>
            )}
        </section>
    );
};

export default AuctionsNearMeSearchInput;
